import { parseJsonp } from "../../utils/parseJsonp";
import TAXI_PARAMS from "../../utils/build";

const CURRENT_DATE = new Date().toISOString();
const API_BASE_URL = `${TAXI_PARAMS.WEBAPI_URL}/handlers/sedi/api.ashx?`;
const API_KEY = TAXI_PARAMS.WEBSERVER_APIKEY || ''; 

if (!TAXI_PARAMS.WEBSERVER_APIKEY) {
  console.warn('WEBSERVER_APIKEY is not defined');
}

if (!TAXI_PARAMS.WEBAPI_URL) {
  console.warn('WEBAPI_URL is not defined');
}

export interface ICost {
  Value: number;
  Unit: string;
}

export interface IProperty {
  Key: string;
  Type: string;
  PropertyType: string;
  SequenceNumber: number;
  ID: number;
  Name: string;
}

export interface ISpecs {
  ID: string;
  Key: string;
  Name: string;
  IsProperty: boolean;
  Type: string;
  Cost: ICost;
  Property: IProperty;
}

interface IRequestParams {
  [key: string]: string | number | boolean;
}

export default class WebServerApi {
  private readonly apiBase = API_BASE_URL;
  private readonly apiKey = API_KEY;

  private validateApiKey(): void {
    if (!this.apiKey) {
      throw new Error('WEBSERVER_APIKEY is required but not provided');
    }
  }

  private async fetchData(endpoint: string, params: IRequestParams = {}) {
    this.validateApiKey();

    const urlParams = new URLSearchParams();
    
    urlParams.append('apikey', this.apiKey);
    
    Object.entries(params).forEach(([key, value]) => {
      urlParams.append(key, String(value));
    });

    const url = `${this.apiBase}${endpoint}${urlParams.toString()}`;
    
    try {
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const jsonp = await response.text();
      return parseJsonp(jsonp);
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  async getInformation(lang: string): Promise<ISpecs[]> {
    const data = await this.fetchData('', {
      lang,
      q: 'get_tariffs'
    });
    return data.Specs || [];
  }

  async getActivationKey(phone: string, lang: string) {
    return this.fetchData('', {
      q: 'get_activation_key',
      usertype: 'Customer',
      lang,
      phone
    });
  }

  async activationKey(personalKey: string, lang: string, name: string = '?') {
    return this.fetchData('', {
      q: 'activation_key',
      usertype: 'customer',
      lang,
      name,
      actkey: personalKey
    });
  }

  async addOrder(
    userKey: string,
    phone: string,
    lang: string,
    tariff: string,
    cost: number,
    address0: {
      city: string;
      street: string;
      house: string;
      object: string;
      lat: number;
      lon: number;
    },
    address1: {
      city: string;
      street: string;
      house: string;
      object: string;
      lat: number;
      lon: number;
    }
  ) {
    return this.fetchData('', {
      q: 'add_order',
      date: CURRENT_DATE,
      name: '?',
      phone,
      tariff,
      cost,
      ordertype: 'rush',
      cashless: false,
      lang,
      userkey: userKey,
      city0: address0.city,
      street0: address0.street,
      house0: address0.house,
      object0: address0.object,
      lat0: address0.lat,
      lon0: address0.lon,
      city1: address1.city,
      street1: address1.street,
      house1: address1.house,
      object1: address1.object,
      lat1: address1.lat,
      lon1: address1.lon
    });
  }

  async getOrders(userKey: string, lang: string) {
    return this.fetchData('', {
      lang,
      q: 'get_orders',
      userkey: userKey
    });
  }

  async cancelOrder(orderId: number, userKey: string, lang: string) {
    return this.fetchData('', {
      orderid: orderId,
      lang,
      q: 'cancel_order',
      userkey: userKey
    });
  }
}

export const createWebServerApi = () => new WebServerApi();